import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiTag, FiShare2, FiGift } = FiIcons;

const HowItWorks = () => {
  const steps = [
    {
      step: "01",
      icon: FiPlus,
      title: "Create Your List",
      description: "Start by creating a new gift list for any occasion. Give it a name and description to let gifters know what it's for."
    },
    {
      step: "02", 
      icon: FiTag,
      title: "Add Items & Categorize",
      description: "Import products from any store by pasting URLs. Mark each item as a 'Want' or 'Need' to help gifters prioritize."
    },
    {
      step: "03",
      icon: FiShare2,
      title: "Share with Gifters",
      description: "Send your list to friends and family with a simple link. They can view all items and see what matters most to you."
    },
    {
      step: "04",
      icon: FiGift,
      title: "Let Them Choose",
      description: "Gifters can browse your wants and needs, then purchase directly from the list. You'll get exactly what you wanted!"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 px-6">
      <div className="container mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="text-gray-800">How It</span>
            <br />
            <span className="bg-gradient-to-r from-rose-primary to-rose-secondary bg-clip-text text-transparent">
              Works
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Four simple steps to create the perfect gift-giving experience for you and your loved ones.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`flex flex-col md:flex-row items-center mb-16 ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
            >
              {/* Content */}
              <div className={`flex-1 ${index % 2 === 1 ? 'md:text-right md:pl-12' : 'md:pr-12'}`}>
                <div className="text-6xl font-bold text-rose-accent/30 mb-4">
                  {step.step}
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  {step.title}
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>

              {/* Icon */}
              <div className="flex-shrink-0 my-8 md:my-0">
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className="w-24 h-24 bg-gradient-to-br from-rose-primary to-rose-secondary rounded-3xl flex items-center justify-center shadow-xl"
                >
                  <SafeIcon icon={step.icon} className="text-white text-4xl" />
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;